import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BREAK_GLASS_MIN_REASON_LENGTH } from "@/lib/break-glass";

interface BreakGlassModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
  title?: string;
  description?: string;
}

export function BreakGlassModal({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  title = "Emergency access (break-the-glass)",
  description = "Use only when immediate patient care requires access outside your normal patient list. Your justification is audited and privacy officers are alerted.",
}: BreakGlassModalProps) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const valid = trimmed.length >= BREAK_GLASS_MIN_REASON_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        stackLayer="above-panels"
        className="gap-0 p-0 sm:max-w-[520px] overflow-hidden bg-card rounded-xl border border-border shadow-2xl"
      >
        <div className="px-6 pt-6 pb-4 pr-14">
          <div className="flex gap-4 items-start">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <DialogHeader className="space-y-2 text-left">
              <DialogTitle className="text-base font-bold">{title}</DialogTitle>
              <DialogDescription className="text-sm leading-relaxed">
                {description}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="mt-5 rounded-xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 flex gap-3 dark:bg-amber-500/10 dark:border-amber-500/30">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-relaxed dark:text-amber-200">
              Emergency access lasts up to 1 hour. All chart activity during this period is logged in{" "}
              <span className="font-semibold">Settings → Audit Logs</span>.
            </p>
          </div>

          <div className="mt-5 space-y-2">
            <Label
              htmlFor="break-glass-reason"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Clinical justification (required)
            </Label>
            <Textarea
              id="break-glass-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Covering on-call - trauma patient in ED not on my panel."
              rows={4}
              className="resize-none text-sm"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Minimum {BREAK_GLASS_MIN_REASON_LENGTH} characters ({trimmed.length}/
              {BREAK_GLASS_MIN_REASON_LENGTH})
            </p>
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/25 px-6 py-4 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={!valid || loading}
            onClick={() => onConfirm(trimmed)}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Activating…
              </>
            ) : (
              "Activate emergency access"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
